import React, { useState, useEffect, useMemo } from "react";
import { Modal, Checkbox, Button, Select, notification, Spin, Input } from "antd";
import { useMutation, useQuery } from "@tanstack/react-query";
import { fetchSpecSections, bulkCreateSpecSections } from "../services/specSectionsAPI";
import { useAuth } from "../hooks/useAuth";
import queryClient from "../queryClient";

const CopySpecSectionsFromProjectModal = ({ visible, onClose }) => {
  const { selectedProject, currentUser } = useAuth();
  const projectId = selectedProject?.project_id;
  const [sourceProjectId, setSourceProjectId] = useState(null);
  const [selectedSectionIds, setSelectedSectionIds] = useState([]);
  const [searchText, setSearchText] = useState("");

  const sourceProjects = (currentUser?.projects || []).filter(
    (p) => p.project_id !== projectId
  );

  const getSectionId = (section) => section.id || section._id;

  const { data: targetData } = useQuery({
    queryKey: ["specSections", projectId, "all"],
    queryFn: () => fetchSpecSections(projectId, 1, 10000),
    enabled: visible && !!projectId,
  });

  const { data: sourceData, isLoading } = useQuery({
    queryKey: ["specSections", sourceProjectId, "all"],
    queryFn: () => fetchSpecSections(sourceProjectId, 1, 10000),
    enabled: visible && !!sourceProjectId,
  });

  const existingSectionNumbers = useMemo(() => {
    return new Set((targetData?.specSections || []).map((s) => s.section_number));
  }, [targetData]);

  const allSections = useMemo(() => {
    return (sourceData?.specSections || []).map((section) => ({
      ...section,
      is_already_added: existingSectionNumbers.has(section.section_number),
    }));
  }, [sourceData, existingSectionNumbers]);

  const filteredSections = useMemo(() => {
    if (!searchText) return allSections;
    const searchLower = searchText.toLowerCase();
    return allSections.filter(
      (section) =>
        section.section_number?.toLowerCase().includes(searchLower) ||
        section.title?.toLowerCase().includes(searchLower)
    );
  }, [allSections, searchText]);

  const sectionsToCopy = useMemo(() => {
    return selectedSectionIds
      .map((id) => allSections.find((s) => getSectionId(s) === id))
      .filter((s) => s && !s.is_already_added);
  }, [selectedSectionIds, allSections]);

  useEffect(() => {
    setSelectedSectionIds([]);
    setSearchText("");
  }, [sourceProjectId]);

  useEffect(() => {
    if (!visible) {
      setSourceProjectId(null);
      setSelectedSectionIds([]);
      setSearchText("");
    }
  }, [visible]);

  const copyMutation = useMutation({
    mutationFn: (sections) =>
      bulkCreateSpecSections({
        project_id: projectId,
        spec_sections: sections.map((s) => ({
          section_number: s.section_number,
          title: s.title,
        })),
      }),
    onSuccess: (data) => {
      notification.success({
        message: "Specification sections copied successfully",
        description: data.detail || `Successfully copied ${data.added} section(s).`,
      });
      queryClient.invalidateQueries(["specSections", projectId]);
      handleClose();
    },
    onError: (error) => {
      const errorDetail = error.response?.data?.detail;
      const errorMessage =
        typeof errorDetail === "string"
          ? errorDetail
          : errorDetail?.message || error.message || "An unknown error occurred";
      notification.error({
        message: "Failed to copy specification sections",
        description: errorMessage,
      });
    },
  });

  const handleSelectAll = () => {
    const filterableAvailableIds = filteredSections
      .filter((s) => !s.is_already_added)
      .map(getSectionId);

    const allFilteredSelected =
      filterableAvailableIds.length > 0 &&
      filterableAvailableIds.every((id) => selectedSectionIds.includes(id));

    if (allFilteredSelected) {
      setSelectedSectionIds((prev) =>
        prev.filter((id) => !filterableAvailableIds.includes(id))
      );
    } else {
      setSelectedSectionIds((prev) => [
        ...new Set([...prev, ...filterableAvailableIds]),
      ]);
    }
  };

  const handleToggleSection = (sectionId) => {
    const section = allSections.find((s) => getSectionId(s) === sectionId);
    if (section?.is_already_added) return;

    setSelectedSectionIds((prev) =>
      prev.includes(sectionId)
        ? prev.filter((id) => id !== sectionId)
        : [...prev, sectionId]
    );
  };

  const handleCopy = () => {
    if (!sourceProjectId) {
      notification.warning({
        message: "No project selected",
        description: "Please select a source project to copy specification sections from.",
      });
      return;
    }
    if (sectionsToCopy.length === 0) {
      notification.warning({
        message: "No sections selected",
        description: "Please select at least one section that hasn't been added yet.",
      });
      return;
    }
    copyMutation.mutate(sectionsToCopy);
  };

  const handleClose = () => {
    setSourceProjectId(null);
    setSelectedSectionIds([]);
    setSearchText("");
    onClose();
  };

  if (!selectedProject || !projectId) return null;

  return (
    <Modal
      title="Copy Specification Sections From a Source Project"
      open={visible}
      onCancel={handleClose}
      footer={null}
      centered
      width={700}
      destroyOnClose
    >
      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Source Project
        </label>
        {sourceProjects.length === 0 ? (
          <p className="text-gray-500 text-sm">
            You need at least one other project to copy specification sections from.
          </p>
        ) : (
          <Select
            placeholder="Select a project (e.g. 123 Main St)"
            value={sourceProjectId || undefined}
            onChange={setSourceProjectId}
            style={{ width: "100%" }}
            options={sourceProjects.map((p) => ({
              value: p.project_id,
              label: p.project_name || p.name || p.project_id,
            }))}
          />
        )}
      </div>

      {sourceProjectId && (
        <>
          <div className="mb-4">
            <div className="flex justify-start items-center mb-3">
              <Button type="link" onClick={handleSelectAll} style={{ padding: 0 }}>
                {filteredSections.filter((s) => !s.is_already_added).length > 0 &&
                filteredSections
                  .filter((s) => !s.is_already_added)
                  .every((s) => selectedSectionIds.includes(getSectionId(s)))
                  ? "Deselect All"
                  : "Select All"}
              </Button>
            </div>
            <Input.Search
              placeholder="Search by section number or title..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              allowClear
            />
          </div>

          <div
            style={{
              maxHeight: "400px",
              overflowY: "auto",
              border: "1px solid #d9d9d9",
              borderRadius: "4px",
              padding: "8px",
            }}
          >
            {isLoading ? (
              <div className="text-center py-8">
                <Spin size="large" />
                <p className="mt-4">Loading specification sections...</p>
              </div>
            ) : filteredSections.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                {searchText
                  ? "No sections found matching your search."
                  : "The selected project has no specification sections."}
              </div>
            ) : (
              <div className="space-y-2">
                {filteredSections.map((section) => {
                  const sectionId = getSectionId(section);
                  return (
                    <div
                      key={sectionId}
                      className={`flex items-start p-2 rounded ${
                        section.is_already_added
                          ? "opacity-60 cursor-not-allowed bg-gray-100"
                          : "hover:bg-gray-50 cursor-pointer"
                      }`}
                      onClick={() =>
                        !section.is_already_added && handleToggleSection(sectionId)
                      }
                    >
                      <Checkbox
                        checked={
                          selectedSectionIds.includes(sectionId) ||
                          section.is_already_added
                        }
                        disabled={section.is_already_added}
                        onClick={(e) => e.stopPropagation()}
                        onChange={(e) => {
                          e.stopPropagation();
                          if (!section.is_already_added) handleToggleSection(sectionId);
                        }}
                        className="mr-3 mt-1"
                      />
                      <div className="flex-1">
                        <div className="font-medium">
                          {section.section_number} - {section.title}
                          {section.is_already_added && (
                            <span className="ml-2 text-xs text-gray-500 font-normal">
                              (Already in project)
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </>
      )}

      <div className="flex justify-end gap-2 mt-4">
        <Button onClick={handleClose}>Cancel</Button>
        <Button
          type="primary"
          onClick={handleCopy}
          loading={copyMutation.isPending}
          disabled={
            sourceProjects.length === 0 ||
            !sourceProjectId ||
            sectionsToCopy.length === 0
          }
        >
          Copy Sections ({sectionsToCopy.length})
        </Button>
      </div>
    </Modal>
  );
};

export default CopySpecSectionsFromProjectModal;
